import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, Layers, ShieldCheck, Ruler, Hammer, Sparkles, BadgeDollarSign,
  Palette, FileCheck, MapPin, Check,
} from "lucide-react";
import { BUSINESS, API_URL } from "../config";
import { CtaBand, offerNote } from "../components/CtaBand";
import { WoodSwatch } from "../components/WoodSwatch";

const HERO_IMG = "https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwdGltYmVyJTIwZmxvb3Jpbmd8ZW58MHx8fHwxNzg3MjI0NDQwfDA&ixlib=rb-4.1.0&q=85";

const WHY_ITEMS = [
  { icon: Layers, title: "Quality 7.5 mm hybrid flooring", text: "Durable waterproof boards made for everyday family homes." },
  { icon: ShieldCheck, title: "Rigid SPC core", text: "A strong, stable core that handles daily life." },
  { icon: Ruler, title: "Attached underlay", text: "Pre-attached underlay for a simpler install." },
  { icon: Hammer, title: "Professional installation", text: "Experienced installers who treat your home with respect." },
  { icon: Sparkles, title: "Clean and accurate workmanship", text: "Neat cuts, tidy edges and a proper finish." },
  { icon: BadgeDollarSign, title: "Competitive pricing", text: "Affordable hybrid flooring without cutting corners." },
  { icon: Palette, title: "Modern timber colours", text: "A range of colours to suit modern Australian homes." },
  { icon: FileCheck, title: "Warranty included", text: "Warranty included where applicable for peace of mind." },
  { icon: MapPin, title: "Free quotes across Melbourne", text: "Clear, written quotes for Melbourne and surrounds." },
];

const STEPS = [
  "Send us your flooring measurements, location and photos.",
  "Choose your preferred flooring colour.",
  "Receive a clear, written quote.",
  "Book a suitable installation date.",
];

export default function Home() {
  const [colours, setColours] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/colours`).then((r) => setColours(r.data.filter((c) => (c.style || "straight") === "straight").slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section className="relative" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Newly installed hybrid timber flooring in a modern Melbourne living room" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/15" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-28 md:py-40">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E8B06F] mb-4 fade-up">{BUSINESS.slogan}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-white max-w-2xl fade-up" style={{ animationDelay: "80ms" }}>
            Quality Hybrid Flooring, Professionally Installed
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/85 max-w-xl leading-relaxed fade-up" style={{ animationDelay: "160ms" }}>
            Transform your home with durable 7 mm hybrid flooring supplied and installed by Zum Flooring.
            Servicing Melbourne and surrounding suburbs.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 fade-up" style={{ animationDelay: "240ms" }}>
            <Link
              to="/get-a-free-quote"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C87A38] px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-[#A6632B] focus:outline-none focus:ring-2 focus:ring-white"
              data-testid="hero-quote-button"
            >
              Get a Free Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/hybrid-flooring"
              className="inline-flex items-center justify-center rounded-full bg-white/10 backdrop-blur border border-white/40 px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
              data-testid="hero-view-flooring-button"
            >
              View Our Flooring
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#4A3018]" data-testid="offer-banner">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E8B06F]">Current offer</p>
          <p className="mt-2 text-2xl sm:text-3xl font-semibold text-white font-heading" data-testid="offer-text">
            7 mm Hybrid Flooring – Supply &amp; Installation from <span className="text-[#E8B06F]">$39.50 per m²</span>
          </p>
          <p className="mt-3 text-sm text-white/70 max-w-2xl mx-auto">
            Includes installation, standard scotia and warranty where applicable. {offerNote}
          </p>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C87A38]">Why choose us</p>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-semibold max-w-2xl">Why Choose Zum Flooring?</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_ITEMS.map((item, i) => (
              <div
                key={item.title}
                className={`group border border-[#E5E5E5] rounded-xl p-7 bg-white transition-all duration-300 hover:shadow-lg hover:-translate-y-1 fade-up ${
                  i === 0 ? "sm:col-span-2 lg:col-span-1" : ""
                }`}
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`why-card-${i}`}
              >
                <item.icon className="w-7 h-7 text-[#C87A38]" />
                <h3 className="mt-4 text-lg font-semibold font-heading">{item.title}</h3>
                <p className="mt-2 text-sm text-[#525252] leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#FAFAFA] py-20 md:py-24" data-testid="home-colours-preview">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C87A38]">Flooring colours</p>
              <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-semibold">Modern timber colours for every home</h2>
              <p className="mt-4 text-base text-[#525252] max-w-xl leading-relaxed">
                Our 7.5 mm SPC hybrid flooring is available in a selection of modern timber colours to suit your space.
              </p>
            </div>
            <Link
              to="/colours"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#C87A38] hover:text-[#A6632B] transition-colors shrink-0"
              data-testid="home-colours-view-all"
            >
              View all colours <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {colours.map((c, i) => (
              <Link
                key={c.id}
                to="/colours"
                className="group border border-[#E5E5E5] rounded-xl overflow-hidden bg-white transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                data-testid={`home-colour-card-${i}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  {c.image_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${c.image_url}`} alt={`${c.name} hybrid flooring`} className="w-full h-full object-cover" />
                  ) : (
                    <WoodSwatch light={c.light} dark={c.dark} seed={i + 3} className="w-full h-full transition-transform duration-500 group-hover:scale-105" />
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold font-heading text-base">{c.name}</h3>
                  <p className="text-xs text-[#525252] mt-1">{c.code} · {c.thickness || "7.5 mm"} SPC hybrid</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C87A38]">How it works</p>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-semibold">Getting new floors is simple</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step, i) => (
              <div key={i} className="relative border border-[#E5E5E5] rounded-xl p-7 bg-white fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`step-card-${i}`}>
                <span className="text-4xl font-bold font-heading text-[#C87A38]/25">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-3 text-base font-medium leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#FAFAFA] py-20 md:py-24" data-testid="home-included-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden">
            <img
              src="https://images.pexels.com/photos/38291042/pexels-photo-38291042.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Professional flooring installer at work in a Melbourne home"
              className="w-full h-full object-cover aspect-[4/3]"
              data-testid="home-install-image"
            />
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C87A38]">Supply &amp; installation</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-semibold">Flooring supply and installation, sorted in one go</h2>
            <p className="mt-5 text-base text-[#525252] leading-relaxed">
              We supply the 7 mm hybrid boards and install them from start to finish — including subfloor checks,
              standard scotia and clean-up. Prefer to supply your own boards or need installation only? We can help with that too.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Free measure and written quote",
                "Subfloor inspection before installation",
                "Standard scotia for a clean finish",
                "Warranty included where applicable",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-base">
                  <Check className="w-5 h-5 text-[#C87A38] shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/services"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#4A3018] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#3a2512] focus:outline-none focus:ring-2 focus:ring-[#C87A38]"
              data-testid="home-services-button"
            >
              See our services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
